import { Component, createRef } from 'react';
import {
  Animated,
  BackHandler,
  FlatList,
  RefreshControl,
  ToastAndroid,
  ViewToken,
} from 'react-native';

import AndroidBack from '../../boot/nav/AndroidBack';
import { OrientationContext } from '../../boot/OrientationContext';
import { isWebPlatform } from '../../boot/utils';
import { AnalyticsEvent } from '../../constants/AnalyticsEvent';
import { isCypress } from '../../constants/Cypress';
import ElementType from '../../constants/ElementType';
import { GET_LIST_ITEM_NUM } from '../../constants/layout';
import { MktType } from '../../constants/MktType';
import type { WithIsFocusedProps } from '../../decorators/withIsFocused';
import { RefreshControlProps } from '../../decorators/withRefreshControl';
import { GET_BANNER_WIDTH } from '../../elements/Banner/styles';
import NoMoreItemsView from '../../elements/NoMoreItemsView';
import { FlatListWithCover } from '../../elements/ScrollWithCover';
import { translate } from '../../i18n';
import {
  logAnalyticsEvent,
  logImpressionBatch as logImpressionBatchAction,
} from '../../redux/actions/analyticsLog';
import { getMemberProfileActions } from '../../redux/actions/getMemberProfileActions';
import { getPromotionalThemesActions } from '../../redux/actions/getPromotionalThemesActions';
import {
  RecommendProductsPayload,
  getRecommendProductsActions,
} from '../../redux/actions/getRecommendProductsActions';
import { getTopKeywordsActions } from '../../redux/actions/getTopKeywordsActions';
import { getListItemPerPage } from '../../redux/api/utils';
import { ItemTypes, ListLoadingTypes } from '../../types/Common';
import { Impression } from '../../types/CTR';
import { RecommendProduct } from '../../types/RecommendProductsResponse';
import { ActivationEvents } from '../../utils/abTestingHelpers';
import coverLoadedProxy from '../../utils/coverLoadedProxy';
import ItemSources from '../../utils/ItemSources';
import { getActiveRoute } from '../../utils/navigationService';
import InAppMessage from '../InAppMessage';
import ListItem from '../ListItem';
import WebPushSubscribeModal from '../WebPushSubscribeModal';
import Banner, { HomeBannerType } from './Banner';
import CategoriesList from './CategoryListSection';
import Header from './Header';
import { HEADER_KEYS, HeaderItems } from './headerItems';
import homePageProxy from './homePageProxy';
import ListHeader from './ListHeader';
import { OnboardingList, OnboardingTitle } from './OnboardingSection';
import { OnboardingRefMethods } from './OnboardingSection/OnboardingList';
import OnSaleList from './OnSaleList';
import PopupGroup from './PopupGroup';
import PromoteItem, { layoutName as promoteLayoutName } from './PromoteItem';
import PromoteSection, {
  Methods as PromoteSectionMethods,
} from './PromoteSection';
import ShortcutButtons from './ShortcutButtons';
import styles, { LIST_GAP, S } from './styles';
import { TopRatedList, TopRatedListTitle } from './TopRatedListSection';
import { TopRatedRefMethods } from './TopRatedListSection/TopRatedList';
import { TopSalesList, TopSalesListTitle } from './TopSalesListSection';
import { TopSalesRefMethods } from './TopSalesListSection/TopSalesList';
import { HandleItemImpression, OnViewableItemsChanged } from './types';
import {
  checkShouldImpression,
  getItemBaseWidth,
  getListItemStyle,
  getPitNumberAtTheSection,
} from './utils';

interface Props extends RefreshControlProps, WithIsFocusedProps {
  isLogin: boolean;
  itemMargin: number;
  page: number;
  promoteItems: string[];
  recommendProducts: string[];
  isFetchingRecommendProducts: boolean;
  hasMoreRecommendProducts: boolean;
  getTopKeyWords: typeof getTopKeywordsActions.request;
  getPromoteItems: typeof getPromotionalThemesActions.request;
  getMemberProfile: typeof getMemberProfileActions.request;
  getRecommendProducts: typeof getRecommendProductsActions.request;
  logEvent: typeof logAnalyticsEvent;
  logImpressionBatch: typeof logImpressionBatchAction;
  scrollToTop: () => void;
  updateFirehoseLogStateForDebug: () => void;
  checkItemViewed: (Map, string) => boolean;
}

interface State {}

const keyExtractor = item => item;
const recommendListLayoutName = 'index_recommend';

export default class HomePage extends Component<Props, State> {
  static contextType = OrientationContext;
  static defaultProps: Partial<Props> = {
    itemMargin: 5,
    updateFirehoseLogStateForDebug: () => undefined,
    checkItemViewed: (_map, _id) => false,
  };
  scrollY = new Animated.Value(0);
  recommendProductSources = new ItemSources<RecommendProduct>({
    productKey: product => `${product.id}`,
    productSource: product => product.source,
  });
  impressionList = createRef<{
    clearImpressionAccumulatedItems: () => void;
    accumulatedItems: Map<string, number>;
    listCurrent: FlatList<any>;
  }>();
  headerStartIndex: Record<string, number> = {};
  onboardingRef = createRef<OnboardingRefMethods>();
  homeBannerRef = createRef<HomeBannerType>();
  topSalesListRef = createRef<TopSalesRefMethods>();
  topRatedListRef = createRef<TopRatedRefMethods>();
  promoteSectionRef = createRef<PromoteSectionMethods>();
  isFromDeepLink = false;
  hasReloadedForDeepLink = false;
  isAndroidBackButtonPressedToExit = false;
  exitAndroidTimer?: ReturnType<typeof setTimeout>;

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    const { logEvent } = this.props;

    this.fetchData('new');
    logEvent({ logEvent: AnalyticsEvent.ViewHome, data: {} });
    homePageProxy.scrollToTop = this.scrollToTop;

    if (isWebPlatform) {
      this.activateOptimizeExperiments();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { isFocused } = this.props;

    if (!prevProps.isFocused && isFocused) {
      if (this.isFromDeepLink && !this.hasReloadedForDeepLink) {
        this.hasReloadedForDeepLink = true;
        this.getRecommendProducts({ loadingType: 'reload' });
      }
    }
  }

  componentWillUnmount = () => {
    this.clearTimer();
  };

  fetchData = (loadingType: ListLoadingTypes) => {
    const { getPromoteItems } = this.props;

    this.getRecommendProducts({ loadingType });
    this.checkOnboardingStatus();
    getPromoteItems({ loadingType });
  };

  getRecommendProducts = (args: Partial<RecommendProductsPayload>) => {
    const { getRecommendProducts } = this.props;
    const perPage = getListItemPerPage();

    getRecommendProducts({
      success: this.recommendProductSources.processSource,
      loadingType: 'new',
      page: 1,
      perPage,
      ...args,
    });
  };

  scrollToTop = () => {
    this.impressionList.current?.listCurrent?.scrollToOffset({ offset: 0 });
  };

  handleAndroidBack = () => {
    const { routeName } = getActiveRoute() || {};

    if (routeName !== 'Home') {
      return false;
    }

    if (this.isAndroidBackButtonPressedToExit) {
      return BackHandler.exitApp();
    } else {
      this.isAndroidBackButtonPressedToExit = true;

      ToastAndroid.show(
        translate('press-again-to-exit-app'),
        ToastAndroid.SHORT
      );

      this.exitAndroidTimer = setTimeout(() => {
        this.isAndroidBackButtonPressedToExit = false;
      }, 2000);
    }
  };

  clearTimer = () => {
    if (this.exitAndroidTimer) {
      clearTimeout(this.exitAndroidTimer);
    }
  };

  setIsFromDeepLink = (isFromDeepLink: boolean) => {
    this.isFromDeepLink = isFromDeepLink;
  };

  renderPromoteItems = (item: string, index: number) => {
    const { checkItemViewed } = this.props;
    const itemIndex = getPitNumberAtTheSection({
      item,
      indexInFlatList: index,
      headerStartIndex: this.headerStartIndex,
    });
    const numColumns = GET_LIST_ITEM_NUM();

    return (
      <PromoteItem
        id={item}
        key={item}
        index={itemIndex}
        numColumns={numColumns}
        showLoading={/blank/.test(`${item}`)}
        layoutName={promoteLayoutName}
        shouldDisplayImpressionForDebug={checkItemViewed(
          this.impressionList.current?.accumulatedItems,
          item
        )}
      />
    );
  };

  renderSpecialForYouItem = (item: string, index: number) => {
    const { itemMargin, checkItemViewed } = this.props;

    const itemIndex = getPitNumberAtTheSection({
      item,
      indexInFlatList: index,
      headerStartIndex: this.headerStartIndex,
    });
    const wrapperStyle = getListItemStyle(itemIndex);
    const source = this.recommendProductSources.get(item);

    return (
      <ListItem
        index={itemIndex}
        key={item}
        id={item}
        showLoading={/blank/.test(`${item}`)}
        itemWidth={getItemBaseWidth(LIST_GAP - itemMargin)}
        itemMargin={itemMargin}
        layoutName={recommendListLayoutName}
        wrapperStyle={wrapperStyle}
        source={source}
        shouldDisplayImpressionForDebug={checkItemViewed(
          this.impressionList.current?.accumulatedItems,
          item
        )}
        linesOfTitle={1}
        showSKUTag
        showVendor
        isHomePageClick
      />
    );
  };

  renderItem = ({ item, index }: { item: string; index: number }) => {
    const { isFocused } = this.props;

    switch (item) {
      case HeaderItems.HomeBanner:
        return (
          <Banner
            ref={this.homeBannerRef}
            key={item}
            isFocused={this.props.isFocused}
          />
        );
      case HeaderItems.ShortcutButtons:
        return <ShortcutButtons key={item} />;
      case HeaderItems.CategoryList:
        return <CategoriesList key={item} />;
      case HeaderItems.OnboardingTitle:
        return <OnboardingTitle key={item} />;
      case HeaderItems.OnboardingList:
        return this.renderOnboardingList(item);
      case HeaderItems.OnSale:
        return <OnSaleList key={item} />;
      case HeaderItems.TopSalesTitle:
        return <TopSalesListTitle key={item} />;
      case HeaderItems.TopSales:
        return (
          <TopSalesList
            key={item}
            ref={this.topSalesListRef}
            isFocused={isFocused}
          />
        );
      case HeaderItems.SpecialForYouTitle:
        return (
          <S.SpecialForYouTitle key={item}>
            {translate('special-for-you')}
          </S.SpecialForYouTitle>
        );
      case HeaderItems.TopRatedTitle:
        return <TopRatedListTitle key={item} />;
      case HeaderItems.TopRated:
        return <TopRatedList key={item} ref={this.topRatedListRef} />;
      case HeaderItems.SectionPromote:
        return (
          <PromoteSection
            ref={this.promoteSectionRef}
            key={item}
            promoteItems={this.props.promoteItems}
          />
        );
      case 'none':
        return null;
      default: {
        return this.renderSpecialForYouItem(item, index);
      }
    }
  };

  renderFooter = () => {
    const { hasMoreRecommendProducts } = this.props;

    if (hasMoreRecommendProducts) {
      return null;
    }

    return (
      <NoMoreItemsView
        containerStyle={styles.noMoreItemsView}
        text={translate('no-more-personal-recommend')}
        useActionBtn
        onActionBtnPress={this.scrollToTop}
        actionBtnText={translate('shopping-other-categories')}
        actionBtnWebProps={{ accessibilityRole: 'button' }}
      />
    );
  };

  handleRecommendProductsItemImpression: HandleItemImpression = (
    acc,
    item,
    pitNumber
  ) => {
    const itemType = ItemTypes.USER_SUBMIT;
    const source = this.recommendProductSources.get(item);

    return acc.concat({
      elementType: ElementType.Item,
      id: item,
      itemType,
      layoutName: recommendListLayoutName,
      mktType: MktType.Image,
      pitNumber,
      source,
    });
  };

  checkOnboardingStatus = () => {
    const { isLogin, getMemberProfile } = this.props;

    // index info 目前的回應為暫時用於是否顯示 onboarding 的判斷，因與用戶有關，故只能登入者才能 call
    // 等之後會員系統做起來後，會淘汰掉這隻
    if (isLogin) {
      getMemberProfile();
    }
  };

  renderOnboardingList = key => {
    const { isFocused } = this.props;

    return (
      <OnboardingList
        key={key}
        ref={this.onboardingRef}
        isFocused={isFocused}
      />
    );
  };

  onRefresh = () => {
    const { onRefreshHOC, getTopKeyWords } = this.props;
    const list = this.impressionList.current;

    onRefreshHOC();
    this.fetchData('reload');
    getTopKeyWords({});

    if (typeof list?.clearImpressionAccumulatedItems === 'function') {
      list.clearImpressionAccumulatedItems();
    }
  };

  onEndReached = () => {
    const {
      isFetchingRecommendProducts,
      hasMoreRecommendProducts,
      page,
      isFocused,
    } = this.props;

    if (!isFocused) {
      return;
    }

    if (hasMoreRecommendProducts && !isFetchingRecommendProducts) {
      const nextPage = page + 1;
      const payload = {
        page: nextPage,
        loadingType: 'loadMore' as ListLoadingTypes,
      };

      this.getRecommendProducts(payload);
    }
  };

  handleCoverLoadEnd = () => {
    coverLoadedProxy.Home = true;
  };

  handleSetSectionVisibility = (viewableItems: ViewToken[]) => {
    const runList = [
      {
        name: HeaderItems.HomeBanner,
        setFunc: this.homeBannerRef.current?.setIsVisible,
      },
      {
        name: HeaderItems.OnboardingList,
        setFunc: this.onboardingRef.current?.setIsVisible,
      },
      {
        name: HeaderItems.TopSales,
        setFunc: this.topSalesListRef.current?.setIsVisible,
      },
      {
        name: HeaderItems.TopRated,
        setFunc: this.topRatedListRef.current?.setIsVisible,
      },
      {
        name: HeaderItems.SectionPromote,
        setFunc: this.promoteSectionRef.current?.setIsVisible,
      },
    ];

    runList.forEach(({ name, setFunc }) => {
      const onView = !!viewableItems.find(
        viewableItem => viewableItem.item === name
      );

      if (typeof setFunc === 'function') {
        setFunc(onView);
      }
    });
  };

  onViewableItemsChanged: OnViewableItemsChanged = ({ viewableItems }) => {
    if (!this.impressionList.current) {
      return;
    }

    this.handleSetSectionVisibility(viewableItems);

    const { logImpressionBatch, updateFirehoseLogStateForDebug } = this.props;
    const items = viewableItems.reduce<Impression[]>((acc, cur) => {
      const { key, index } = cur;
      const accumulated =
        this.impressionList.current!.accumulatedItems.has(key);
      const shouldImpression = checkShouldImpression(key);

      if (!shouldImpression || accumulated) {
        return acc;
      }

      const pitNumber =
        getPitNumberAtTheSection({
          item: key,
          indexInFlatList: index!,
          headerStartIndex: this.headerStartIndex,
        }) + 1;

      this.impressionList.current!.accumulatedItems.set(key, 1);

      return this.handleRecommendProductsItemImpression(acc, key, pitNumber);
    }, []);

    if (items.length > 0) {
      logImpressionBatch({ data: items });
      // force update to log impression
      updateFirehoseLogStateForDebug();
    }
  };

  handlePutAdditionalBlocks = (listDataAry: string[], val: string) => {
    const numColumns = GET_LIST_ITEM_NUM();

    /**
     * 因 FlatList 會根據 "numColumns" 給的值，將 item 群組在一起，例如 "numColumns" 為 2，則 item 會兩兩為一列。
     * 這樣在動態的商品數量上，排版很不受控。
     * 只要經計算後，數量不符當前排版數量 (numColumns)，則在 list data 中加入 "none" 當佔位符。
     */
    while (listDataAry.length % numColumns !== 0) {
      listDataAry.push('none');
      this.headerStartIndex[val] += 1;
    }
  };

  getListData = () => {
    const { promoteItems, recommendProducts } = this.props;

    const data = HEADER_KEYS.reduce<string[]>((result, val) => {
      this.headerStartIndex[val] = result.length;
      this.handlePutAdditionalBlocks(result, val);

      if (val === HeaderItems.PromoteItems) {
        result.push(...promoteItems);
      } else if (val === HeaderItems.SpecialForYouList) {
        result.push(...recommendProducts);
      } else {
        result.push(val);
      }

      return result;
    }, []);

    return data;
  };

  activateOptimizeExperiments = () => {
    if (window && window.dataLayer) {
      const experiments = [];

      experiments.forEach(name => {
        if (name) {
          window.dataLayer.push({
            event: ActivationEvents[name],
          });
        }
      });
    }
  };

  /**
   * 1. HomePage 的 impression 資料在這裡的 onViewableItemsChanged 處理並送出
   * 2. FlatListWithImpression 本身也會透過 dataProducer 處理資料並送出
   * 3. 在上個 commit 之後，FlatListWithImpression 中的 onViewableItemsChanged 會
   *    呼叫 props.onViewableItemsChanged
   *
   * 基於上述，我們需要將 dataProducer 設為 undefined 以避免不預期的 impression 資料被送出，
   * FlatListWithImpression 已處裡若不存在 dataProducer 則不會自行做 impression 的處理與送出
   *
   * TODO: 預期可將 1. 的處理移除，改用 2. FlatListWithImpression 本身處理並送事件，
   */
  undefinedProducer = undefined;

  render() {
    const { showRefreshing, logEvent } = this.props;
    const data = this.getListData();
    const numColumns = GET_LIST_ITEM_NUM();
    const onScroll = Animated.event(
      [{ nativeEvent: { contentOffset: { y: this.scrollY } } }],
      { useNativeDriver: false }
    );

    return (
      <AndroidBack handleAndroidBack={this.handleAndroidBack}>
        <Header animateScrollY={this.scrollY} logEvent={logEvent} />
        <PopupGroup setIsFromDeepLink={this.setIsFromDeepLink} />
        {isWebPlatform && !isCypress && <InAppMessage testID="lightBox" />}
        {isWebPlatform && !isCypress && <WebPushSubscribeModal />}
        <FlatListWithCover
          data={data}
          columnWrapperStyle={styles.listColumnWrapperStyle}
          withImpression
          dataProducer={this.undefinedProducer}
          keyExtractor={keyExtractor}
          layoutName={recommendListLayoutName}
          numColumns={numColumns}
          key={numColumns}
          ListHeaderComponent={ListHeader}
          ListFooterComponent={this.renderFooter}
          onEndReached={this.onEndReached}
          ref={this.impressionList}
          onScroll={onScroll}
          onViewableItemsChanged={this.onViewableItemsChanged}
          renderItem={this.renderItem}
          coverSource={require('./assets/img_background_default.png')}
          coverHeight={GET_BANNER_WIDTH() * (165 / 375)}
          coverResizeMode="stretch"
          onCoverLoadEnd={this.handleCoverLoadEnd}
          refreshControl={
            <RefreshControl
              onRefresh={this.onRefresh}
              refreshing={showRefreshing}
            />
          }
        />
      </AndroidBack>
    );
  }
}
